// The Volume detail tabs (ui-spec §6.11): what it holds and who has it, its committed revisions, and what storage cost.
// Browsing files is a client of the `uploads` grant (`nodus volume ls|get|put`), so the page points at those commands
// instead of listing files.
import { collectionPath, errorView, subresourcePath, useApi } from "@nodus/api";
import {
  CodeSnippet,
  cliCommand,
  ErrorState,
  formatAbsolute,
  formatBytes,
  formatCount,
  formatMoney,
  KeyValueTable,
  Section,
  StatCard,
  StatGrid,
  Table,
  TableCard,
  TD,
  TH,
  THead,
  TimeCell,
  TR,
  usePrefs,
} from "@nodus/ui";
import { DetailSection } from "../../app/templates";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";
import { quantityBytes, sizeLabel, sourceLabel, usedBy, type Scope, type Volume, type VolumeRevision } from "./model";

type Props = { volume: Volume; scope: Scope };

export function OverviewTab({ volume, scope }: Props) {
  const prefs = usePrefs();
  const { spec = {}, status = {} } = volume;
  const used = quantityBytes(status.used);
  const cap = quantityBytes(spec.size);
  const name = volume.metadata.name;
  const importing = status.phase === "Pending" && status.import;
  const cmd = (args: string[]) => cliCommand(args, scope, { omitScope: prefs.omitScope });
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-[var(--nd-section-gap)]">
      <StatGrid>
        <StatCard label="Used" value={used !== undefined ? formatBytes(used) : undefined} caption={cap ? `of ${formatBytes(cap)} limit` : undefined} unknownReason="Nothing is committed yet." live />
        <StatCard
          label="Latest revision"
          value={status.revision ? `#${status.revision}` : undefined}
          caption={status.lastCommitTime ? formatAbsolute(status.lastCommitTime) : undefined}
          unknownReason="Nothing is committed yet."
          live
        />
        <StatCard
          label="Stored"
          value={status.storage ? formatBytes(status.storage.bytes) : undefined}
          caption={status.storage ? `${formatBytes(status.storage.billedBytes)} billed` : undefined}
          unknownReason="Reported after the first commit."
        />
        <StatCard valueSize="text" label="Mounted by" value={usedBy(volume) ?? "Nothing"} live />
      </StatGrid>
      {importing ? (
        <Section title="Import">
          <p className="text-ui text-fg-secondary" data-testid="import-progress">
            Importing from {sourceLabel(spec.source)}: {formatBytes(status.import?.bytes ?? 0)} in {formatCount(status.import?.files ?? 0)} files so far.
          </p>
        </Section>
      ) : null}
      <Section title="Configuration">
        <KeyValueTable
          rows={[
            { label: "Access mode", value: spec.accessMode ?? "—", field: "spec.accessMode", raw: spec.accessMode },
            { label: "Size limit", value: sizeLabel(spec.size), field: "spec.size", raw: spec.size },
            { label: "Source", value: sourceLabel(spec.source), field: "spec.source" },
            { label: "Commit interval", value: spec.accessMode === "ReadOnlyMany" ? "Never commits" : (spec.commitInterval ?? "—"), field: "spec.commitInterval" },
            { label: "Revisions kept", value: String(spec.revisionHistoryLimit ?? "—"), field: "spec.revisionHistoryLimit" },
            { label: "Delete when unused", value: spec.ttlAfterUnused ?? "Never", field: "spec.ttlAfterUnused" },
            { label: "Import cost limit", value: spec.maxCostUSD ? formatMoney(spec.maxCostUSD) : "None", field: "spec.maxCostUSD" },
          ]}
        />
      </Section>
      <DetailSection id="cli" title="Use from the CLI" collapsed>
        <CodeSnippet
          variants={[
            {
              id: "cli",
              label: "CLI",
              code: [
                cmd(["volume", "put", name, "./dataset", "/train"]),
                cmd(["volume", "ls", name, "/train"]),
                cmd(["volume", "get", name, "/train/part-0.parquet", "./"]),
              ].join("\n"),
            },
          ]}
        />
      </DetailSection>
    </div>
  );
}

export function RevisionsTab({ volume, scope }: Props) {
  const { client } = useApi();
  const prefs = usePrefs();
  const name = volume.metadata.name;
  // A new commit moves status.revision, which re-reads the list.
  const q = useQuery({
    queryKey: [scope.org, scope.project, "volume-revisions", name, volume.status?.revision ?? 0],
    queryFn: () => client.get<{ items: VolumeRevision[] }>(subresourcePath("Volume", name, "revisions", scope.project), { org: scope.org }),
  });
  if (q.error) return <ErrorState error={errorView(q.error)} onRetry={() => void q.refetch()} />;
  const items = q.data?.items ?? [];
  if (!items.length) {
    return (
      <p className="text-ui text-fg-muted">
        {q.isLoading ? "Loading revisions…" : <>No revisions yet. <code className="font-mono">{cliCommand(["volume", "put", name, "./dataset"], scope, { omitScope: prefs.omitScope })}</code> commits the first one.</>}
      </p>
    );
  }
  return (
    <Section title="Revisions" count={items.length}>
      <TableCard>
        <Table aria-label="Revisions">
          <THead>
            <TR>{["Revision", "Created", "Size", "Files", "Origin", "Created by"].map((h) => <TH key={h}>{h}</TH>)}</TR>
          </THead>
          <tbody>
            {items.map((r, i) => (
              <TR key={r.revision}>
                <TD className="tabular-nums">
                  #{r.revision}
                  {i === 0 ? <span className="ml-2 text-small text-fg-muted">Latest</span> : null}
                </TD>
                <TD><TimeCell time={r.time} /></TD>
                <TD className="tabular-nums">{formatBytes(r.size)}</TD>
                <TD className="tabular-nums">{formatCount(r.files)}</TD>
                <TD>{r.origin}</TD>
                <TD className="font-mono text-mono">{r.createdBy ?? "—"}</TD>
              </TR>
            ))}
          </tbody>
        </Table>
      </TableCard>
    </Section>
  );
}

/** The UsageSummary of `GET usagerecords?groupBy=day,meter`: one row per day and meter, summed by the server. */
type UsageSummary = { rows: { group: { day?: string; meter?: string }; quantity?: string; amountUSD: string }[]; totalUSD: string };

export function CostTab({ volume, scope }: Props) {
  const { client } = useApi();
  const uid = volume.metadata.uid;
  // Storage is sampled hourly, so a month holds hundreds of lines per Volume: ask for the daily sums, which stay small
  // and exact. The list answers a watch with 405, so this is read once, not watched.
  const q = useQuery({
    queryKey: [scope.org, scope.project, "volume-usage", uid],
    enabled: Boolean(uid),
    queryFn: () => client.get<UsageSummary>(collectionPath("UsageRecord"), { org: scope.org, query: { groupBy: "day,meter", fieldSelector: `object.uid=${uid}`, since: "30d" } }),
  });
  if (q.error) return <ErrorState error={errorView(q.error)} onRetry={() => void q.refetch()} />;
  const rows = [...(q.data?.rows ?? [])].sort((a, b) => (b.group.day ?? "").localeCompare(a.group.day ?? ""));
  if (!rows.length) return <p className="text-ui text-fg-muted">{q.isLoading ? "Loading usage…" : "No storage charges in the last 30 days. Storage is billed on retained bytes."}</p>;
  return (
    <Section title="Storage usage" aside={`Last 30 days · ${formatMoney(q.data?.totalUSD)}`}>
      <TableCard>
        <Table aria-label="Storage usage">
          <THead>
            <TR>{["Day", "Meter", "Quantity", "Amount"].map((h) => <TH key={h}>{h}</TH>)}</TR>
          </THead>
          <tbody>
            {rows.map((r) => (
              <TR key={`${r.group.day}/${r.group.meter}`}>
                <TD>{r.group.day ?? "—"}</TD>
                <TD className="font-mono text-mono">{r.group.meter ?? "—"}</TD>
                <TD className="tabular-nums">{r.quantity ?? "—"}</TD>
                <TD className="tabular-nums">{formatMoney(r.amountUSD)}</TD>
              </TR>
            ))}
          </tbody>
        </Table>
      </TableCard>
    </Section>
  );
}
